import React, { Fragment, useState } from 'react'
import PropTypes from 'prop-types'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'

import { useStripeCheckout, useSubscribeContext } from 'context'
import { useAlert, useGetCountry } from 'hooks'
import {
  Button,
  MembershipPlanPicker,
  Feedback,
  SubscriptionDisclaimer
} from 'ui/bend'
import { ErrorBoundary } from 'helpers'

import { useActiveMembershipContext } from '../../ActiveMembershipStore'
import { PendingPayment } from '../../../PendingPayment'
import { axios } from '../../../../../api'
import { afterDelay } from '../../../../../utils'

/*
 * After successfully submitting the form, plans are not refreshed and the submit button remains disabled.
 * This is because refreshing the plans would trigger new prices to render to the UI, showing
 * a differnet price than what the user just updated to (e.g. base price vs. discounted price),
 * leading to confusion around what the end user will actually be charged.
 */

const RenewStripeForm = ({ cta, reactivate = false }) => {
  const getStripe = useStripeCheckout()

  const { actions, initialValues, state, validators } = useSubscribeContext()
  const { handleError, refreshMembershipInfo } = useActiveMembershipContext()
  const setAlert = useAlert()
  const [showPendingPayment, setShowPendingPayment] = useState(false)
  const country = useGetCountry(false)

  const handleSubmit = async (values) => {
    try {
      const res = await actions.membership.handleSubmit({
        state,
        values: {
          ...values,
          reactivate,
          country
        },
        checkout: false
      })

      if (res) {
        if (res.data?.checkoutSessionId) {
          // Stripe Checkout flow
          // Changes are tracked on /subscribe/welcome after successful redirect
          const stripe = await getStripe()
          const { error } = await stripe.redirectToCheckout({
            sessionId: res.data.checkoutSessionId
          })
          if (error) {
            handleError(error)
          }
        } else {
          refreshMembershipInfo()
          afterDelay(500)
            .then(() => axios.get('/payments/pending_intent'))
            .then((res) => {
              if (!res?.data?.clientSecret) {
                refreshMembershipInfo()
                setAlert({
                  message: 'We have updated your membership.'
                })
              }
              setShowPendingPayment(!!res?.data?.clientSecret)
            })
            .catch((e) => handleError(e))
        }
      }
    } catch (error) {
      handleError(error)
    }
  }

  return (
    <Fragment>
      <Formik
        initialValues={initialValues}
        validationSchema={Yup.object().shape(validators.membership)}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values }) => (
          <Form
            style={{
              display: 'flex',
              flexDirection: 'column',
              padding: '0px 15px 15px'
            }}
            id='settings-membership-form'
          >
            {state.membership.plans && state.membership.plans.length > 0 ? (
              <Fragment>
                {!reactivate && <MembershipPlanPicker />}
                {!!values.selectedPlan && (
                  <SubscriptionDisclaimer cta={cta} reactivate={reactivate} />
                )}
                <Button
                  disabled={!values.selectedPlan}
                  processing={isSubmitting}
                  alignSelf={['stretch', 'flex-end']}
                  mt='24px'
                  type='submit'
                >
                  {cta}
                </Button>
              </Fragment>
            ) : (
              <Feedback className='error'>
                There was a network hiccup. Please refresh in a moment or
                contact support@alomoves.com for assistance.
              </Feedback>
            )}
          </Form>
        )}
      </Formik>
      {showPendingPayment && (
        <PendingPayment
          refresh={true}
          onSuccess={() => refreshMembershipInfo()}
        />
      )}
    </Fragment>
  )
}

RenewStripeForm.propTypes = {
  cta: PropTypes.string.isRequired,
  reactivate: PropTypes.bool
}

const SafeRenewStripeForm = (props) => (
  <ErrorBoundary>
    <RenewStripeForm {...props} />
  </ErrorBoundary>
)

export default SafeRenewStripeForm
